import { useInstructorsUnapprovedData } from '@features/instructors/contexts/unapproved/useInstructorsUnapprovedData';
import * as React from 'react';

import type {
  InstructorSection,
  ManageInstructorsSelectionContextValue,
  ManageInstructorsSelectionProviderProps,
} from './ManageInstructorsSelectionContext.types';

const ManageInstructorsSelectionContext = React.createContext<
  ManageInstructorsSelectionContextValue | undefined
>(undefined);

const ManageInstructorsSelectionProvider = React.memo(
  ({
    children,
  }: Readonly<ManageInstructorsSelectionProviderProps>): React.ReactElement => {
    const { data } = useInstructorsUnapprovedData();

    const hasInitialized = React.useRef(false);
    const [selectedIds, setSelectedIds] = React.useState<
      Record<InstructorSection, Set<string>>
    >(() => ({
      groundSchool: new Set(),
      simulator: new Set(),
      simCheck: new Set(),
    }));

    React.useEffect(() => {
      if (!data || hasInitialized.current) return;
      hasInitialized.current = true;
      setSelectedIds({
        groundSchool: new Set(data.groundSchool.map(i => i.instructorId)),
        simulator: new Set(data.simulator.map(i => i.instructorId)),
        simCheck: new Set(data.simCheck.map(i => i.instructorId)),
      });
    }, [data]);

    const handleSelectionChange = React.useCallback(
      (
        section: InstructorSection,
        instructorId: string,
        selected: boolean
      ): void => {
        setSelectedIds(prev => {
          const next = new Set(prev[section]);
          if (selected) next.add(instructorId);
          else next.delete(instructorId);
          return { ...prev, [section]: next };
        });
      },
      []
    );

    const selectAll: Record<InstructorSection, () => void> = React.useMemo(
      () => ({
        groundSchool: () =>
          setSelectedIds(prev => ({
            ...prev,
            groundSchool: new Set(
              data?.groundSchool.map(i => i.instructorId) ?? []
            ),
          })),
        simulator: () =>
          setSelectedIds(prev => ({
            ...prev,
            simulator: new Set(data?.simulator.map(i => i.instructorId) ?? []),
          })),
        simCheck: () =>
          setSelectedIds(prev => ({
            ...prev,
            simCheck: new Set(data?.simCheck.map(i => i.instructorId) ?? []),
          })),
      }),
      [data]
    );

    const deselectAll: Record<InstructorSection, () => void> = React.useMemo(
      () => ({
        groundSchool: () =>
          setSelectedIds(prev => ({ ...prev, groundSchool: new Set() })),
        simulator: () =>
          setSelectedIds(prev => ({ ...prev, simulator: new Set() })),
        simCheck: () =>
          setSelectedIds(prev => ({ ...prev, simCheck: new Set() })),
      }),
      []
    );

    const hasSelection =
      selectedIds.groundSchool.size > 0 ||
      selectedIds.simulator.size > 0 ||
      selectedIds.simCheck.size > 0;

    const contextValue = React.useMemo<ManageInstructorsSelectionContextValue>(
      () => ({
        selectedIds,
        hasSelection,
        handleSelectionChange,
        selectAll,
        deselectAll,
      }),
      [selectedIds, hasSelection, handleSelectionChange, selectAll, deselectAll]
    );

    return (
      <ManageInstructorsSelectionContext value={contextValue}>
        {children}
      </ManageInstructorsSelectionContext>
    );
  }
);

ManageInstructorsSelectionProvider.displayName =
  'ManageInstructorsSelectionProvider';
export {
  ManageInstructorsSelectionProvider,
  ManageInstructorsSelectionContext,
};
